import styled from '@emotion/styled';
import {
  Box,
  Grid,
  ListItemText,
  Menu,
  MenuItem,
  Paper as MuiPaper,
  Typography,
} from '@mui/material';
import { useContext, useMemo, useState } from 'react';

import { GridRowSelectionModel } from '@mui/x-data-grid';
import { concat, isEmpty } from 'lodash';
import { useFormContext } from 'react-hook-form';
import CustomCheckbox from '../../components/CustomTableCheckbox';
import { Dialog } from '../../components/dialogs/Dialog';
import { JobBenchmarkingTable } from '../../components/table/JobBenchmarkingTable';
import { TablePlaceholder } from '../../components/TablePlaceholder';
import { JobBenchmarkingContext } from '../../contexts/JobBenchmarkingContext';
import { IJobBenchmarking } from '../../interfaces/Job';
import { useLocale } from '../../providers/LocaleProvider';
import AttributeIcons from '../../resources/images/icons-attribute.svg';
import { KLAYO_COLORS } from '../../themes';
import { JobBenchMarkingType } from '../../utilities/validationSchema';

export type CompetenciesResultTabProps = {
  theme: any;
  results?: IJobBenchmarking;
};

const CompetenciesResultTab = ({ theme, results }: CompetenciesResultTabProps) => {
  const [actionTarget, setActionTarget] = useState<null | HTMLElement>(null);
  const [currentRow, setCurrentRow] = useState<any>(null);
  const [requiredTasks, setRequiredTasks] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);

  const { setValue, watch } = useFormContext<JobBenchMarkingType>();
  const {
    setShowMatched,
    setShowUnmatched,
    showMatched,
    showUnmatched,
    showAccompaniedCompetencies,
    setShowAccompaniedCompetencies,
  } = useContext(JobBenchmarkingContext);
  const { translate } = useLocale();

  const renamedCompetencies = watch('renamedCompetencies') || [];
  const addedStandardMatchedCompetencies = watch('addedStandardMatchedCompetencies') || [];
  const addedStandardUnmatchedCompetencies = watch('addedStandardUnmatchedCompetencies') || [];

  const addedStandardUnmatchedTasks = watch('addedStandardUnmatchedTasks') || [];

  const addedStandardMatchedTaskCompetencies = watch('addedStandardMatchedTaskCompetencies') || [];

  const matchedYourCompetencies = useMemo(() => {
    // Drop locked teaser rows: this legacy tab has no blur/disable UI, so over-limit
    // isExtra rows would render as normal selectable rows. The paywall is conveyed by
    // the separate "contact to upgrade" panel (hasLimitedAccess).
    const matchedTasks = concat(
      results?.competencyComparison.exactMatches || [],
      results?.competencyComparison.fuzzyMatches || []
    ).filter((match) => !match.standardLibraryJobCompetency?.isExtra);

    return (
      matchedTasks.map((match) => {
        const yourJobId = match.organizationJobCompetency.attributeDefinitionId;
        const industryStandardJobId =
          match.standardLibraryJobCompetency.standardLibraryCompetencyId;

        const isRenamed = !!renamedCompetencies.find((t) => t.id === yourJobId);

        const isAdded = !!addedStandardMatchedCompetencies.find(
          (t) => t.id === industryStandardJobId
        );

        return {
          id: `${yourJobId}_${industryStandardJobId}`,
          yourJob: match.organizationJobCompetency.attributeDefinitionName,
          industryStandardJob: match.standardLibraryJobCompetency.standardLibraryCompetencyName,
          yourJobId,
          industryStandardJobId,
          isRenamed,
          isAdded,
        };
      }) || []
    );
  }, [results, renamedCompetencies, addedStandardMatchedCompetencies]);

  const ignoredMatchedCompetencies = useMemo(() => {
    return (
      matchedYourCompetencies
        .filter((match) => {
          return addedStandardMatchedCompetencies.some((t) => t.id === match.industryStandardJobId);
        })
        .map((t) => ({
          id: t.yourJobId,
          name: t.yourJob,
        })) || []
    );
  }, [matchedYourCompetencies, addedStandardMatchedCompetencies]);

  const organizationCompetencies = useMemo(() => {
    return concat(
      results?.competencyComparison.onlyInOrganization.map((task) => ({
        id: task.attributeDefinitionId,
        name: task.name,
      })) || [],
      ignoredMatchedCompetencies
    );
  }, [results, ignoredMatchedCompetencies]);

  const industryStandardCompetencies = useMemo(() => {
    return (
      results?.competencyComparison.onlyInStandardLibrary
        .filter((c) => !c.isExtra)
        .map((c) => ({
          id: c.id,
          name: c.name,
        })) || []
    );
  }, [results]);

  const industryStandardTasks = useMemo(() => {
    return (
      results?.taskComparison.onlyInStandardLibrary
        .filter((task) => !task.isExtra)
        .map((task) => ({
          id: task.id,
          name: task.name,
          taskCompetencies: task.taskCompetencies?.map((c) => ({
            id: c.id,
            name: c.name,
          })),
        })) || []
    );
  }, [results]);

  const matchedCompetenciesCount = useMemo(() => {
    return matchedYourCompetencies.length || 0;
  }, [matchedYourCompetencies]);

  const unmatchedYourCompetenciesCount = useMemo(() => {
    return organizationCompetencies.length || 0;
  }, [organizationCompetencies]);

  const unmatchedIndustryStandardCompetenciesCount = useMemo(() => {
    return industryStandardCompetencies.length || 0;
  }, [industryStandardCompetencies]);

  const unmatchedCompetenciesCount = useMemo(() => {
    return unmatchedYourCompetenciesCount + unmatchedIndustryStandardCompetenciesCount;
  }, [unmatchedYourCompetenciesCount, unmatchedIndustryStandardCompetenciesCount]);

  const isMultipleMatchedCompetencies = useMemo(
    () => matchedCompetenciesCount > 1,
    [matchedCompetenciesCount]
  );
  const isMultipleUnmatchedCompetencies = useMemo(
    () => unmatchedCompetenciesCount > 1,
    [unmatchedCompetenciesCount]
  );
  const isMultipleUnmatchedYourCompetencies = useMemo(
    () => unmatchedYourCompetenciesCount > 1,
    [unmatchedYourCompetenciesCount]
  );
  const isMultipleUnmatchedIndustryStandardCompetencies = useMemo(
    () => unmatchedIndustryStandardCompetenciesCount > 1,
    [unmatchedIndustryStandardCompetenciesCount]
  );

  const addedStandardMatchedTaskCompetenciesCount = useMemo(
    () => addedStandardMatchedTaskCompetencies.length,
    [addedStandardMatchedTaskCompetencies]
  );
  const isMultipleAddedStandardMatchedTaskCompetencies = useMemo(
    () => addedStandardMatchedTaskCompetenciesCount > 1,
    [addedStandardMatchedTaskCompetenciesCount]
  );

  const hasLimitedAccess = useMemo(
    () => results?.competencyComparison.hasLimitedAccess || false,
    [results?.competencyComparison.hasLimitedAccess]
  );

  const handleActionMenu = (event: any, row: any) => {
    setActionTarget(event.currentTarget);
    setCurrentRow(row);
  };

  const onCloseActionMenu = () => {
    setActionTarget(null);
    setCurrentRow(null);
  };

  const onToggleRenameCompetency = () => {
    if (!currentRow) return;

    let updatedRenamedCompetencies;
    if (currentRow.isRenamed) {
      // Revert: remove from renamedCompetencies
      updatedRenamedCompetencies = renamedCompetencies.filter((t) => t.id !== currentRow.yourJobId);
    } else {
      // Rename: add to renamedTasks with industry standard name
      updatedRenamedCompetencies = [
        ...renamedCompetencies,
        { id: currentRow.yourJobId, name: currentRow.industryStandardJob },
      ];
    }
    setValue('renamedCompetencies', updatedRenamedCompetencies, { shouldDirty: true });
    onCloseActionMenu();
  };

  const onTakeNewCompetency = () => {
    if (!currentRow) return;

    if (currentRow.isAdded) {
      // Revert: remove from addedStandardMatchedCompetencies
      const updatedAddedTasks = addedStandardMatchedCompetencies.filter(
        (task) => task.id !== currentRow.industryStandardJobId
      );
      setValue('addedStandardMatchedCompetencies', updatedAddedTasks, { shouldDirty: true });
    } else {
      // Add the industry standard task as a new task if not already present
      if (
        !addedStandardMatchedCompetencies.some(
          (task) => task.id === currentRow.industryStandardJobId
        )
      ) {
        const newTask = {
          id: currentRow.industryStandardJobId,
          name: currentRow.industryStandardJob,
        };
        setValue(
          'addedStandardMatchedCompetencies',
          [...addedStandardMatchedCompetencies, newTask],
          {
            shouldDirty: true,
          }
        );
      }
    }
    onCloseActionMenu();
  };

  const onAddUnmatchedCompetencies = (newSelection: GridRowSelectionModel) => {
    const selectedCompetencies = industryStandardCompetencies.filter((task) =>
      newSelection.includes(task.id)
    );

    // Find competencies that are being deselected
    const deselectedCompetencies = addedStandardUnmatchedCompetencies.filter(
      (competency) =>
        !newSelection.includes(competency.id) &&
        !matchedYourCompetencies.find(
          (matchedCompetency) => matchedCompetency.industryStandardJobId === competency.id
        )
    );

    // For each deselected competency, check if it's required by any selected task
    const requiredTasksForDeselected = deselectedCompetencies.flatMap((competency) => {
      return addedStandardUnmatchedTasks
        .map((task) => industryStandardTasks.find((t) => t.id === task.id))
        .filter((task) => task?.taskCompetencies?.some((tc) => tc.id === competency.id));
    });

    if (requiredTasksForDeselected.length > 0) {
      // Show dialog with required tasks
      setRequiredTasks(requiredTasksForDeselected as any);
      return;
    }

    setValue('addedStandardUnmatchedCompetencies', selectedCompetencies, {
      shouldDirty: true,
    });
  };

  return (
    <>
      <Box display={'flex'} flexDirection={'column'} gap={3}>
        {!!matchedCompetenciesCount && (
          <Paper>
            <Box p={3} display={'flex'} flexDirection={'column'} gap={2}>
              <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                <Typography fontWeight={600} variant='body1'>
                  {translate(
                    isMultipleMatchedCompetencies
                      ? 'settingsViews.jobBenchmarking.matchedCompetencies_plural'
                      : 'settingsViews.jobBenchmarking.matchedCompetencies',
                    { count: matchedCompetenciesCount }
                  )}
                </Typography>

                <PButton
                  onClick={() =>
                    setShowMatched({
                      ...showMatched,
                      competency: !showMatched.competency,
                    })
                  }
                >
                  {showMatched.competency
                    ? translate('settingsViews.jobBenchmarking.hideDetails')
                    : translate('settingsViews.jobBenchmarking.showDetails')}
                </PButton>
              </Box>

              {!showMatched.competency ? (
                <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
                  {translate(
                    isMultipleMatchedCompetencies
                      ? 'settingsViews.jobBenchmarking.weFoundMatchedCompetencies_plural'
                      : 'settingsViews.jobBenchmarking.weFoundMatchedCompetencies',
                    { count: matchedCompetenciesCount }
                  )}
                </Typography>
              ) : (
                <JobBenchmarkingTable
                  columns={[
                    {
                      type: JobBenchmarkingTable.columns.yourJob,
                      sortable: false,
                      label: translate('settingsViews.jobBenchmarking.yourJob'),
                    },
                    {
                      type: JobBenchmarkingTable.columns.industryStandardJob,
                      sortable: false,
                      cellClassName: 'pl-4',
                      headerClassName: 'pl-4',
                      label: translate('settingsViews.jobBenchmarking.klayoIntelligenceJob'),
                    },
                    {
                      type: JobBenchmarkingTable.columns.actions,
                      label: '',
                    },
                  ]}
                  rows={matchedYourCompetencies}
                  pagination={false}
                  paper={false}
                  theme={theme}
                  selectable={false}
                  dense={true}
                  showFooter={false}
                  onActionMenu={handleActionMenu}
                  sx={{
                    '& .MuiDataGrid-columnHeaders': {
                      borderBottom: 'none', // remove border-bottom in header
                    },
                  }}
                />
              )}
            </Box>
          </Paper>
        )}

        {!!unmatchedCompetenciesCount && (
          <Paper>
            <Box p={3} display={'flex'} flexDirection={'column'} gap={2}>
              <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                <Typography fontWeight={600} variant='body1'>
                  {translate(
                    isMultipleUnmatchedCompetencies
                      ? 'settingsViews.jobBenchmarking.unmatchedCompetencies_plural'
                      : 'settingsViews.jobBenchmarking.unmatchedCompetencies',
                    { count: unmatchedCompetenciesCount }
                  )}
                </Typography>

                <PButton
                  onClick={() =>
                    setShowUnmatched({
                      ...showUnmatched,
                      competency: !showUnmatched.competency,
                    })
                  }
                >
                  {showUnmatched.competency
                    ? translate('settingsViews.jobBenchmarking.hideDetails')
                    : translate('settingsViews.jobBenchmarking.showDetails')}
                </PButton>
              </Box>

              {!showUnmatched.competency ? (
                <Typography color={KLAYO_COLORS.GreyGR500} variant='body2'>
                  {translate('settingsViews.jobBenchmarking.weFoundUnmatchedCompetencies', {
                    yourCount: unmatchedYourCompetenciesCount,
                    standardCount: unmatchedIndustryStandardCompetenciesCount,
                  })}
                </Typography>
              ) : (
                <Grid container>
                  <Grid item xs={6}>
                    <JobBenchmarkingTable
                      columns={[
                        {
                          type: JobBenchmarkingTable.columns.name,
                          sortable: false,
                          label: translate('settingsViews.jobBenchmarking.yourJob').toUpperCase(),
                        },
                      ]}
                      rows={organizationCompetencies}
                      pagination={false}
                      paper={false}
                      theme={theme}
                      selectable={false}
                      dense={true}
                      showFooter={false}
                      sx={{
                        '& .MuiDataGrid-columnHeaders': {
                          borderBottom: 'none', // remove border-bottom in header
                        },
                      }}
                      noResultsComponent={
                        <TablePlaceholder
                          text={translate(
                            'settingsViews.jobBenchmarking.noMoreCompetenciesInYourJob'
                          )}
                          icon={<AttributeIcons />}
                        />
                      }
                      noRowsComponent={
                        <TablePlaceholder
                          text={translate(
                            'settingsViews.jobBenchmarking.noMoreCompetenciesInYourJob'
                          )}
                          icon={<AttributeIcons />}
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <JobBenchmarkingTable
                      columns={[
                        {
                          type: JobBenchmarkingTable.columns.name,
                          sortable: false,
                          label: translate(
                            'settingsViews.jobBenchmarking.klayoIntelligenceJob'
                          ).toUpperCase(),
                        },
                        {
                          type: JobBenchmarkingTable.columns.checkbox,
                        },
                      ]}
                      rows={industryStandardCompetencies}
                      pagination={false}
                      paper={false}
                      theme={theme}
                      selectable={false}
                      dense={true}
                      showFooter={false}
                      onActionMenu={handleActionMenu}
                      checkboxSelection
                      slots={{
                        baseCheckbox: CustomCheckbox as React.ComponentType<unknown>,
                      }}
                      rowSelectionModel={addedStandardUnmatchedCompetencies.map((task) => task.id)}
                      onRowSelectionModelChange={onAddUnmatchedCompetencies}
                      sx={{
                        '& .MuiDataGrid-columnHeaders': {
                          borderBottom: 'none', // remove border-bottom in header
                        },
                        '& .MuiDataGrid-columnHeaderCheckbox': {
                          display: 'none !important', // Visually hide header checkbox
                        },
                      }}
                      noResultsComponent={
                        <TablePlaceholder
                          text={translate(
                            'settingsViews.jobBenchmarking.noMoreCompetenciesInStandardJob'
                          )}
                          icon={<AttributeIcons />}
                        />
                      }
                      noRowsComponent={
                        <TablePlaceholder
                          text={translate(
                            'settingsViews.jobBenchmarking.noMoreCompetenciesInStandardJob'
                          )}
                          icon={<AttributeIcons />}
                        />
                      }
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          </Paper>
        )}

        {!isEmpty(addedStandardMatchedTaskCompetencies) && (
          <Paper>
            <Box p={3} display={'flex'} flexDirection={'column'} gap={2}>
              <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                <Typography fontWeight={600} variant='body1'>
                  {translate(
                    isMultipleAddedStandardMatchedTaskCompetencies
                      ? 'settingsViews.jobBenchmarking.newCompetenciesFromTasks_plural'
                      : 'settingsViews.jobBenchmarking.newCompetenciesFromTasks',
                    { count: addedStandardMatchedTaskCompetenciesCount }
                  )}
                </Typography>

                <PButton
                  onClick={() =>
                    setShowAccompaniedCompetencies({
                      ...showAccompaniedCompetencies,
                      competency: !showAccompaniedCompetencies.competency,
                    })
                  }
                >
                  {showAccompaniedCompetencies.competency
                    ? translate('settingsViews.jobBenchmarking.hideDetails')
                    : translate('settingsViews.jobBenchmarking.showDetails')}
                </PButton>
              </Box>

              {!showAccompaniedCompetencies.competency ? (
                <Typography color={KLAYO_COLORS.GreyGR500} variant='body2'>
                  {translate(
                    isMultipleAddedStandardMatchedTaskCompetencies
                      ? 'settingsViews.jobBenchmarking.weIncludedCompetencies_plural'
                      : 'settingsViews.jobBenchmarking.weIncludedCompetencies',
                    { count: addedStandardMatchedTaskCompetenciesCount }
                  )}
                </Typography>
              ) : (
                <JobBenchmarkingTable
                  columns={[
                    {
                      type: JobBenchmarkingTable.columns.name,
                      sortable: false,
                      label: translate('settingsViews.jobBenchmarking.competency').toUpperCase(),
                    },
                  ]}
                  rows={addedStandardMatchedTaskCompetencies}
                  pagination={false}
                  paper={false}
                  theme={theme}
                  selectable={false}
                  dense={true}
                  showFooter={false}
                  sx={{
                    '& .MuiDataGrid-columnHeaders': {
                      borderBottom: 'none', // remove border-bottom in header
                    },
                  }}
                  noResultsComponent={
                    <TablePlaceholder
                      text={translate('settingsViews.jobBenchmarking.noMatchingCompetencies')}
                      icon={<AttributeIcons />}
                    />
                  }
                  noRowsComponent={
                    <TablePlaceholder
                      text={translate('settingsViews.jobBenchmarking.noMatchingCompetencies')}
                      icon={<AttributeIcons />}
                    />
                  }
                />
              )}
            </Box>
          </Paper>
        )}

        {hasLimitedAccess && (
          <Paper>
            <Box p={3} textAlign={'center'}>
              {translate('settingsViews.jobBenchmarking.contactToUpgrade')}
            </Box>
          </Paper>
        )}

        <Menu
          open={actionTarget !== null}
          anchorEl={actionTarget}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          onClose={onCloseActionMenu}
        >
          <MenuItem disabled={currentRow?.isAdded} onClick={onToggleRenameCompetency}>
            <ListItemText>
              {currentRow?.isRenamed
                ? translate('settingsViews.jobBenchmarking.revertCompetencyName')
                : translate('settingsViews.jobBenchmarking.renameCompetencyName')}
            </ListItemText>
          </MenuItem>
          <MenuItem disabled={currentRow?.isRenamed} onClick={onTakeNewCompetency}>
            <ListItemText>
              {currentRow?.isAdded
                ? translate('settingsViews.jobBenchmarking.dontTakeAsNewCompetency')
                : translate('settingsViews.jobBenchmarking.takeAsNewCompetency')}
            </ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      <Dialog
        open={!isEmpty(requiredTasks)}
        theme={theme}
        onClose={() => setRequiredTasks([])}
        title={translate('settingsViews.jobBenchmarking.cannotRemoveCompetency')}
        actions={[
          {
            label: translate('common.cancel'),
            onClick: () => setRequiredTasks([]),
          },
        ]}
      >
        <Box mb={2}>
          <Typography>
            {translate('settingsViews.jobBenchmarking.competencyRequiredByTasks')}
          </Typography>
        </Box>
        <Box>
          {requiredTasks.map((task) => (
            <Typography
              variant='body1'
              sx={{
                color: KLAYO_COLORS.GreyGR500,
                fontWeight: 600,
                fontSize: '.9rem',
              }}
              key={task.id}
            >
              {task.name}
            </Typography>
          ))}
        </Box>
      </Dialog>
    </>
  );
};

export default CompetenciesResultTab;

const PButton = styled.p`
  color: #4d5ef7;
  cursor: pointer;
  font-weight: 600;
  margin: 0;
  z-index: 10;
`;

const Paper = styled(MuiPaper)`
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  overflow: hidden;
  border-radius: 0.875rem;
  border: 2px solid rgba(230, 230, 230, 0.83);
`;
